<template>
  <div class="deduct-list">
    <!-- 扣分登记（企业微信手机端） -->
    <mobile-list-header>
      <!-- 第一行：搜索框占满整行，左输入框 + 右搜索按钮 -->
      <div class="search-bar">
        <div class="search-input">
          <yq-icon class="icon" name="element-ui_search"/>
          <input v-model="searchInfo.searchKey" placeholder="原因/考核项" @keyup.enter="resetSearch"/>
          <yq-icon v-if="searchInfo.searchKey" class="icon clear" name="element-ui_close" @click="clearKey"/>
        </div>
        <div class="search-btn" @click="resetSearch">搜索</div>
      </div>
      <!-- 第二行：查询条件，无背景色，超过一屏横向滑动 -->
      <div class="filter-row">
        <mobile-search-select v-model="searchInfo.employeeId" :query="queryStaffPage" size="small"
                              label-column="employeeName" code-column="employeeId"
                              placeholder="员工" search-placeholder="员工姓名"
                              @change="resetSearch">
          <!-- 职员按「头像 + 名称 / 手机号」两行显示（预览模板 B） -->
          <template #item="{item}">
            <div class="mss-item mss-item-avatar-row">
              <div class="mss-item-avatar">
        <img v-if="item.iconUrl" :src="$getImageUrl(item.iconUrl)" alt=""/>
        <yq-icon v-else name="element-ui_user"/>
              </div>
              <div class="mss-item-info">
        <div class="mss-item-main">{{ item.employeeName }}</div>
        <div class="mss-item-sub">{{ item.employeePhone || "无联系电话" }}</div>
              </div>
            </div>
          </template>
        </mobile-search-select>
        <mobile-month-select v-model="searchInfo.month" placeholder="扣分月份" title="选择扣分月份" @change="resetSearch"/>
      </div>
    </mobile-list-header>
    <mobile-list :loading="loading" :has-more="searchInfo.hasMore" :query-list="queryList" :refresh="resetSearch" ref="mobileList">
      <list-card class="deduct-item" v-for="item in dataList" :key="item.id" :label="item.employeeName || '—'">
        <template #desc>
          <span class="points">-{{ pointsText(item.points) }}</span>
        </template>
        <data-column label="扣分日期">{{ dateOnly(item.deductionDate) }}</data-column>
        <data-column label="考核项">{{ item.itemName || "—" }}</data-column>
        <data-column label="原因">{{ item.reason || "—" }}</data-column>
        <!-- 没有详情页，删除只能放在卡片上 -->
        <div class="card-actions">
          <span class="delete-btn" @click.stop="removeRow(item)">删除</span>
        </div>
      </list-card>
    </mobile-list>
    <!-- 悬浮「新增扣分」按钮：右下角，可拖动 -->
    <mobile-float-button label="新增扣分" icon="element-ui_plus" @click="toCreate"/>
  </div>
</template>

<script>

import {useRoute, useRouter} from "vue-router";
import MobileList from "@/components/qywx/layout/MobileList.vue";
import MobileListHeader from "@/components/qywx/layout/MobileListHeader.vue";
import MobileSearchSelect from "@/components/qywx/data/MobileSearchSelect.vue";
import MobileMonthSelect from "@/components/qywx/data/MobileMonthSelect.vue";
import MobileFloatButton from "@/components/qywx/button/MobileFloatButton.vue";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataColumn from "@/components/qywx/data/DataColumn.vue";
import YqIcon from "@/components/YqIcon.vue";
import deductionApi from "@/api/dz-crm/kpi/deductionApi";
import calendarApi from "@/api/dz-crm/kpi/calendarApi";

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "DL",
  components: {
    YqIcon, DataColumn, ListCard, MobileFloatButton, MobileMonthSelect, MobileSearchSelect,
    MobileListHeader, MobileList
  },
  data() {
    return {
      loading: false,
      searchInfo: {
        searchKey: "",
        employeeId: "",
        // 扣分月份 yyyy-MM（空=全部时间）
        month: "",
        pageNumber: 0,
        pageSize: 20,
        hasMore: true, // 初始为 true，列表挂载后由无限滚动自动触发首次查询
      },
      dataList: [],
    };
  },
  methods: {
    // ---------- 查询 ----------
    queryList() {
      this.loading = true;
      this.searchInfo.pageNumber++;
      deductionApi.queryList({
        // 空值不提交，避免空串影响后端条件
        ...pickFilled(this.searchInfo),
        pageNumber: this.searchInfo.pageNumber,
        pageSize: this.searchInfo.pageSize,
      }).then(res => {
        this.dataList = this.dataList.concat(res.records || []);
        this.searchInfo.hasMore = res.pageCount > this.searchInfo.pageNumber;
        this.loading = false;
        this.$refs.mobileList.hideRefreshTitle();
      }).catch(() => {
        this.loading = false;
      })
    },
    resetSearch() {
      this.searchInfo.pageNumber = 0;
      this.dataList = [];
      this.searchInfo.hasMore = true;
      this.queryList();
    },
    clearKey() {
      this.searchInfo.searchKey = "";
      this.resetSearch();
    },
    /**
     * 员工选择框的数据源。
     * 平台职员接口是一次性返回的数组（非分页），这里包一层适配通用选择组件：
     * 关键字在前端过滤即可 —— 一个商户的平台启用职员就这么多人。
     */
    queryStaffPage(params) {
      return calendarApi.queryEmployeeStaff({}).then(list => {
        const keyword = (params.searchKey || "").trim();
        const records = keyword
            ? (list || []).filter(item => (item.employeeName || "").indexOf(keyword) > -1
                || (item.employeePhone || "").indexOf(keyword) > -1)
            : (list || []);
        return {records, pageCount: 1};
      });
    },
    removeRow(item) {
      this.$confirm("是否确认删除该扣分记录", "提示", {type: "warning"})
          .then(() => deductionApi.delete(item.id))
          .then(() => this.resetSearch())
          .catch(() => {
          });
    },
    // ---------- 展示 ----------
    dateOnly(value) {
      return value ? String(value).slice(0, 10) : "—";
    },
    /** 扣分值：去掉多余的 0（2.0 → 2） */
    pointsText(points) {
      if (points === null || points === undefined || points === "") {
        return "0";
      }
      return String(Number(points));
    },
    toCreate() {
      this.router.push("/qywx/kpi/deduct/detail");
    }
  }
};

/** 只取有值的字段 */
function pickFilled(source) {
  const params = {};
  Object.keys(source).forEach(key => {
    const value = source[key];
    if (value === "" || value === null || value === undefined) {
      return;
    }
    params[key] = value;
  });
  return params;
}
</script>

<style lang="scss" scoped>
.deduct-list{
  height: 100%;
  display: flex;
  flex-direction: column;
  // 悬浮按钮的定位祖先
  position: relative;
}
// 第一行：搜索框占满整行，左输入框 + 右搜索按钮
.search-bar{
  display: flex;
  align-items: center;
  height: 44px;
  padding-left: 12px;
  border-radius: 999px;
  background: var(--yq-background-color);
  overflow: hidden;
  .search-input{
    flex: 1;
    display: flex;
    align-items: center;
    min-width: 0;
    height: 100%;
    padding-right: 12px;
    .icon{
      flex: none;
      color: var(--yq-font-color-desc);
      font-size: var(--yq-font-size-mini-mobile);
      &.clear{
        margin-left: 6px;
      }
    }
    input{
      flex: 1;
      min-width: 0;
      height: 100%;
      margin-left: 6px;
      border: none;
      outline: none;
      background: transparent;
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--yq-font-color-base);
      &::placeholder{
        color: var(--yq-font-color-desc);
      }
    }
  }
  .search-btn{
    flex: none;
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 18px;
    background: var(--el-color-primary);
    color: #FFF;
    font-size: var(--yq-font-size-mini-mobile);
  }
}
// 第二行：查询条件，无背景色，超过一屏横向滑动
.filter-row{
  display: flex;
  align-items: center;
  gap: 18px;
  margin-top: 10px;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar{
    display: none;
  }
  > *{
    flex: none;
  }
}
.deduct-item{
  // 扣分值：标题右侧，红色
  .points{
    flex: none;
    font-size: var(--yq-font-size-large-mobile);
    font-weight: 700;
    color: var(--el-color-danger);
  }
  // 没有详情页，删除只能放在卡片上
  .card-actions{
    display: flex;
    justify-content: flex-end;
    padding-top: 10px;
    border-top: 1px solid var(--yq-border-color);
    .delete-btn{
      padding: 2px 12px;
      border-radius: 4px;
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--el-color-danger);
      border: 1px solid var(--el-color-danger);
    }
  }
}
</style>
